// @flow

import React, { useCallback, useContext } from 'react';
import Cookie from 'js-cookie';
import { Paragraph, Button, Notice } from 'operation-diameter';
import ReactGA from 'react-ga';
import { useHistory } from 'react-router-dom';

import { useCopy } from 'hooks/useCopy';
import { useForm } from 'hooks/useForm';
import {
  businessStructureToCompanyType,
  CompanyType,
  businessStructureRadios,
  yesNoRadios,
  limitedOnlyIntroducerIds
} from 'shared/utils/constants';
import type { Company } from 'shared/utils/types';
import { FormContext } from 'shared/formContext/FormContext';
import { nonEmpty } from 'shared/validations';
import { sendToLogin } from 'shared/utils/helpers';
import EnhancedRadioGroup from 'shared/components/EnhancedRadioGroup';
import CompanySearchInput from 'shared/components/CompanySearchInput';

import styles from './BusinessDetails.module.css';

export const NEXT_GPO_ROUTE = '/contact-details';

const mapCompany = ({
  companyName,
  uniqueCompanyIdentifier,
  companyCodasId
}: Company) => ({
  companyName,
  uniqueCompanyIdentifier,
  companyCodasId
});

const getCompanyFromFields = (fields) =>
  fields && fields.companyName
    ? {
        companyName: fields.companyName,
        uniqueCompanyIdentifier: fields.uniqueCompanyIdentifier,
        companyCodasId: fields.companyCodasId
      }
    : null;

const isValidCompany = (company) =>
  company &&
  company.companyName &&
  company.uniqueCompanyIdentifier &&
  company.companyCodasId;

export const BusinessDetails = () => {
  const { push } = useHistory();
  const sendToFCA = useCallback(() => {
    ReactGA.event({
      category: 'borrower-eligibility-business-details',
      action: 'business details submission',
      label: 'redirecting users to non-GPO'
    });
    window.location.assign('/borrower/loan/apply?referral=gpo');
  }, []);

  const formContext = useContext(FormContext);
  const { getCopy, getCopyJSX } = useCopy('borrower');
  const fields = {
    businessStructure: {
      initialState: formContext.fields.businessStructure,
      validations: [nonEmpty],
      validateOnSet: true
    },
    companyInformation: {
      initialState: getCompanyFromFields(formContext.fields),
      validations: [isValidCompany],
      validateOnSet: true,
      shouldValidate: (_, { businessStructure }) => {
        return (
          businessStructureToCompanyType[businessStructure] ===
            CompanyType.LIMITED ||
          businessStructureToCompanyType[businessStructure] === CompanyType.LLP
        );
      }
    },
    isNonLimitedExbo: {
      initialState: formContext.fields.isNonLimitedExbo || false,
      validations: [nonEmpty],
      validateOnSet: true,
      shouldValidate: (_, { businessStructure }) =>
        businessStructureToCompanyType[businessStructure] ===
        CompanyType.NON_LIMITED
    }
  };

  const {
    businessStructure,
    companyInformation,
    isNonLimitedExbo,
    isFormValid,
    runValidations
  } = useForm({
    fields
  });

  const companyType = businessStructureToCompanyType[businessStructure.value];
  const isLimited = companyType === CompanyType.LIMITED;
  const isLLP = companyType === CompanyType.LLP;
  const isNonLimited = companyType === CompanyType.NON_LIMITED;

  const showCompanySearch = isLimited || isLLP;
  const showExBoField = isNonLimited;

  const redirectByCompanyType = useCallback(() => {
    if (isNonLimitedExbo.value === true) return sendToLogin();
    if (isNonLimited) return sendToFCA();
    if (isLimited || isLLP) return false;
    return true;
  }, [isLimited, isLLP, isNonLimited, isNonLimitedExbo.value, sendToFCA]);

  const onSubmit = useCallback(
    (event: SyntheticInputEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (isFormValid) {
        if (redirectByCompanyType()) return;

        formContext.setFields({
          businessStructure: businessStructure.value,
          isNonLimitedExbo: isNonLimitedExbo.value,
          ...mapCompany(companyInformation.value)
        });

        push(NEXT_GPO_ROUTE);
      }

      runValidations();
    },
    [
      isFormValid,
      runValidations,
      redirectByCompanyType,
      formContext,
      businessStructure.value,
      isNonLimitedExbo.value,
      companyInformation.value,
      push
    ]
  );

  // For customers coming from the Sage landing page, we can't show the partnership and sole trader radio buttons
  let BusinessStructureRadiosCopy = businessStructureRadios;
  const introducerIdCookie = Cookie.get('introducer_id');

  limitedOnlyIntroducerIds.includes(introducerIdCookie) &&
    (BusinessStructureRadiosCopy = businessStructureRadios.filter(
      (businessStructure) =>
        businessStructure.value === 'limited-company' ||
        businessStructure.value === 'limited-liability-partnership'
    ));

  return (
    <div className={styles.businessDetailsWrapper}>
      <form onSubmit={onSubmit} className={styles.form}>
        <div>
          <EnhancedRadioGroup
            legend={getCopy('business-details.title')}
            name='businessStructure'
            radios={BusinessStructureRadiosCopy}
            onChange={(currentTarget) => businessStructure.set(currentTarget)}
            selectedValue={businessStructure.value}
            error={businessStructure.error}
          />
        </div>
        {showCompanySearch ? (
          <CompanySearchInput
            companyType={companyType}
            {...(companyInformation.error && {
              error: companyInformation.error
            })}
            label={getCopy('business-details.business-name.label')}
            heading={getCopy('business-details.business-name.heading')}
            onBlur={() => {
              companyInformation.validate();
              if (companyInformation.value === null) {
                formContext.setFields({
                  businessStructure: businessStructure.value
                });
              }
            }}
            setSelectedCompany={(company) => companyInformation.set(company)}
            selectedCompany={companyInformation.value || null}
          />
        ) : (
          showExBoField && (
            <EnhancedRadioGroup
              legend={getCopy('business-details.existing-borrower.label')}
              name='nonLimitedExbo'
              radios={yesNoRadios}
              onChange={(value) => isNonLimitedExbo.set(value)}
              selectedValue={isNonLimitedExbo.value}
              error={isNonLimitedExbo.error}
            />
          )
        )}
        <Paragraph>{getCopyJSX('business-details.note')}</Paragraph>
        <Button
          size='large'
          type='submit'
          data-cy='continue-button'
          data-testid='submit-button'
          additionalClassnames={styles.submitButton}>
          Continue
        </Button>
        {showCompanySearch && (
          <Notice variant='info'>
            {getCopy('business-details.no_company')}
          </Notice>
        )}
      </form>
    </div>
  );
};

export default BusinessDetails;
